// src/hooks/useAlerts.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import GenericAlert from "@ui-components/Alert/GenericAlert";

type AlertVariant = "confirm" | "success" | "error" | "warning";

type BaseConfig = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: AlertVariant;
};

type Mode = "confirm" | "alert";

const DEFAULTS: Required<Pick<BaseConfig, "confirmLabel" | "cancelLabel" | "variant">> = {
  confirmLabel: "OK",
  cancelLabel: "Cancel",
  variant: "confirm",
};

// Provide a full initial config (title is required)
const INITIAL_CFG: BaseConfig = {
  title: "",
  ...DEFAULTS,
};

export function useAlerts() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("confirm");
  const [cfg, setCfg] = useState<BaseConfig>(INITIAL_CFG);

  // Nullable resolver to avoid "empty function" lint error
  const resolverRef = useRef<((value: boolean) => void) | null>(null);
  const mounted = useRef(true);

  // Defer mount 2 frames to avoid same-tick outside-click issues
  const openNextTick = (fn: () => void) => {
    requestAnimationFrame(() => requestAnimationFrame(fn));
  };

  useEffect(() => {
    // ✅ mark mounted on (re)mount — StrictMode re-mounts components in dev
    mounted.current = true;
  
    return () => {
      // ❌ mark unmounted on cleanup
      mounted.current = false;
      // If unmounted while open, resolve(false) so callers don't hang
      resolverRef.current?.(false);
    };
  }, []);
  

  const confirm = useCallback((input: BaseConfig): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      if (!mounted.current) return resolve(false);
      resolverRef.current = resolve;
      setMode("confirm");
      setCfg({ ...INITIAL_CFG, ...input });
      openNextTick(() => setOpen(true));
    });
  }, []);

  const alert = useCallback((input: BaseConfig): Promise<void> => {
    return new Promise<void>((resolve) => {
      if (!mounted.current) return resolve();
      resolverRef.current = () => resolve();
      setMode("alert");
      setCfg({ ...INITIAL_CFG, ...input, cancelLabel: undefined }); // alerts don't show cancel
      openNextTick(() => setOpen(true));
    });
  }, []);

  const handleConfirm = useCallback(() => {
    resolverRef.current?.(true);
    setOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    resolverRef.current?.(false);
    setOpen(false);
  }, []);

  const Alert = open
    ? createPortal(
        <GenericAlert
          isOpen={true}
          onConfirm={handleConfirm}
          onClose={handleClose}
          title={cfg.title}
          description={cfg.description}
          confirmLabel={cfg.confirmLabel}
          cancelLabel={mode === "confirm" ? cfg.cancelLabel : undefined}
          variant={cfg.variant}
        />,
        document.body
      )
    : null;

  return { confirm, alert, Alert };
}

export type UseAlerts = ReturnType<typeof useAlerts>;
